/**
 * ConfigurationContext — app-wide tenant configuration.
 *
 * The provider fetches `/configuration/` once on mount (via React
 * Query so the cache survives navigation) and exposes the resulting
 * shape plus a few derived formatter helpers. Consumers don't import
 * the service directly — they call {@link useConfiguration} or one of
 * the formatter hooks below.
 *
 * Defaults:
 *   * Before the network call resolves we return {@link DEFAULT_CONFIGURATION}.
 *     This avoids the entire app freezing on `useConfiguration().currency`
 *     during the initial fetch.
 *   * On a network failure we keep returning the defaults and surface
 *     `isError` so the Settings page can show a banner.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  configurationApi,
  DEFAULT_CONFIGURATION,
  type ConfigurationPatch,
  type TenantConfiguration,
} from "@/services/configurationApi";

export const CONFIGURATION_QUERY_KEY = ["configuration"] as const;

export interface ConfigurationContextValue {
  config: TenantConfiguration;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /** Patch helper that wraps the React Query mutation. */
  update: (patch: ConfigurationPatch) => Promise<TenantConfiguration>;
  isUpdating: boolean;
  refetch: () => void;
}

const ConfigurationContext = createContext<ConfigurationContextValue | null>(null);

export function ConfigurationProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: CONFIGURATION_QUERY_KEY,
    queryFn: configurationApi.get,
    // Config is small and rarely changes; keep cached for a while so
    // tab-switching doesn't re-fetch unless it's actually stale.
    staleTime: 5 * 60_000,
    // Don't retry hard — if /configuration/ is unreachable we'd rather
    // fall through to DEFAULT_CONFIGURATION than block the whole app.
    retry: 1,
  });

  const mutation = useMutation({
    mutationFn: configurationApi.patch,
    onSuccess: (next) => {
      // Optimistically replace the cache entry so the rest of the app
      // sees the new values immediately, without re-fetching.
      qc.setQueryData(CONFIGURATION_QUERY_KEY, next);
    },
  });

  const update = useCallback(
    (patch: ConfigurationPatch) => mutation.mutateAsync(patch),
    [mutation],
  );

  const value: ConfigurationContextValue = useMemo(
    () => ({
      config: query.data ?? DEFAULT_CONFIGURATION,
      isLoading: query.isPending,
      isError: query.isError,
      error: (query.error as Error) ?? null,
      update,
      isUpdating: mutation.isPending,
      refetch: () => qc.invalidateQueries({ queryKey: CONFIGURATION_QUERY_KEY }),
    }),
    [query.data, query.isPending, query.isError, query.error, update, mutation.isPending, qc],
  );

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
}

/**
 * Primary consumer hook. Always safe to call within the provider tree.
 *
 * Throws if used outside the provider — surface it loudly so it's
 * obvious during development rather than silently returning defaults.
 */
export function useConfiguration(): ConfigurationContextValue {
  const ctx = useContext(ConfigurationContext);
  if (!ctx) {
    throw new Error(
      "useConfiguration() called outside <ConfigurationProvider>. Wrap your app in App.tsx.",
    );
  }
  return ctx;
}
